<template>
  <!-- BootstrapVueだとCordovaで入力時にスクロールするバグがあったので、生のtextareaにbootstrapで使用 -->
  <textarea
    class="app-textarea form-control"
    :class="isValid"
    :value="value"
    :placeholder="placeholder"
    @input="onInput($event)"
    @blur="onBlur($event)"
    :rows="rows"
    :max-rows="maxRows"
    ref="textarea"
  />
</template>

<script lang="ts">
import { captureException } from "@sentry/vue";
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import { ValidationStateType } from "@/types/Validation";
import { getFormState } from "@/utils/validation";

const MIN_ROWS = 2;

@Component
export default class AppTextArea extends Vue {
  get state() {
    return getFormState(this.validationState);
  }

  get isValid() {
    // false以外の値は関知しないのでこの書き方
    if (this.state === false) return "is-invalid";
    if (this.state) return "is-valid";
    return "";
  }

  @Prop({
    type: String || Number,
    required: true,
    default: ""
  })
  value!: string | number;

  @Prop({
    type: String,
    default: ""
  })
  label!: string;

  @Prop({
    type: String,
    default: ""
  })
  placeholder!: string;

  @Prop({
    type: [String, Number],
    default: "5"
  })
  rows!: string | number;

  @Prop({
    type: [String, Number],
    default: "6"
  })
  maxRows!: string | number;

  @Prop({
    type: Object,
    default: () => ({})
  })
  validationState!: ValidationStateType;

  @Prop({
    type: Boolean,
    default: false
  })
  enableAutoResize!: boolean;

  textareaRef: HTMLTextAreaElement | null = null;

  mounted() {
    this.textareaRef = this.$refs.textarea as HTMLTextAreaElement;
  }

  /**
   * テキストエリアの高さを自動調整する
   * およそ2~maxRows行の間で調整する
   */
  autoResize() {
    if (this.enableAutoResize) {
      const bodyStyle = window.getComputedStyle(document.body);
      try {
        const linePx = Number.parseInt(bodyStyle.lineHeight.slice(0, -2)); // "14px"のpxを取り除く
        const maxRows =
          typeof this.maxRows === "string"
            ? Number.parseInt(this.maxRows)
            : this.maxRows;
        if (this.textareaRef) {
          this.textareaRef.style.height = "0";
          if (this.textareaRef.scrollHeight < linePx * MIN_ROWS) {
            this.textareaRef.style.height = linePx * MIN_ROWS + "px";
          } else if (linePx * maxRows < this.textareaRef.scrollHeight) {
            this.textareaRef.style.height = linePx * maxRows + "px";
          } else {
            this.textareaRef.style.height =
              this.textareaRef.scrollHeight + "px";
          }
        }
      } catch (e) {
        captureException(e as Error);
      }
    }
  }

  /**
   * テキストエリアの高さをリセットする
   * テキストが送信されて空になったときに呼び出したいので、valueの監視をしている
   */
  @Watch("value")
  resetSize() {
    if (this.value === "") {
      if (this.enableAutoResize) {
        const bodyStyle = window.getComputedStyle(document.body);
        try {
          const linePx = Number.parseInt(bodyStyle.lineHeight.slice(0, -2)); // "14px"のpxを取り除く
          if (this.textareaRef) {
            this.textareaRef.style.height = linePx * MIN_ROWS + "px";
          }
        } catch (e) {
          captureException(e as Error);
        }
      }
    }
  }

  onInput(event: Event) {
    this.autoResize();
    const target = event.target as HTMLTextAreaElement;
    this.$emit("input", target.value);
  }

  onBlur(event: FocusEvent) {
    const target = event.target as HTMLTextAreaElement;
    this.$emit("blur", target.value);
  }
}
</script>

<style lang="scss" scoped>
.app-textarea {
  padding: 6px 8px;
  font-size: 14px;
  background: #fff;

  &::placeholder {
    color: $gray-04;
  }
}
</style>
